// Only Insertion Order templates should be surfaced to users. Names like
// "DO NOT USE IO - ..." start with "DO" and are excluded automatically.
export const isIoTemplate = (name: string): boolean => name.trim().toUpperCase().startsWith('IO ');

/* Characters Windows refuses in a filename, plus control characters. A title
   is free text -- "IO - Q1/Q2 Push" would otherwise name a folder. */
// eslint-disable-next-line no-control-regex
const UNSAFE_FILENAME_CHARS = /[\\/:*?"<>|\u0000-\u001F\u007F]/g;

/**
 * Filename an IO downloads as, e.g. `12345 - IO - Tearfund Winter Campaign 2026 - v1.pdf`.
 *
 * The IO's own name with the PipeDrive deal id in front, matching the name the
 * backend's download endpoint gives the file. Without a deal id the name stands
 * alone; without a name, the document id keeps the file nameable at all.
 */
export const ioFileName = (
  document: { documentId: string; messageTitle?: string; dealId?: number }
): string => {
  const name =
    (document.messageTitle ?? '').replace(UNSAFE_FILENAME_CHARS, '-').replace(/\s+/g, ' ').trim() ||
    document.documentId;
  return `${document.dealId != null ? `${document.dealId} - ` : ''}${name}.pdf`;
};

/**
 * The backend always responds with `{ name, message, statusCode, ... }` (see
 * express-error.ts), regardless of whether the error originated in our own auth
 * middleware or was proxied through from an upstream service call (e.g. PandaDoc).
 *
 * Our own auth middleware throws `http-errors` `Unauthorized`, which serialises as
 * `name: "UnauthorizedError"`. A failure proxied from PandaDoc (e.g. a bad/placeholder
 * API key) comes back as `name: "AxiosError"` with the *upstream* status code baked in
 * (frequently also 401) — that is NOT a sign the user's own session has expired, and
 * must never be shown to the user as a raw AxiosError message or bare status code.
 */
export type PandaDocErrorKind = 'sessionExpired' | 'integrationUnavailable' | 'requestInvalid' | 'unknown';

interface BackendErrorBody {
  name?: string;
  message?: string;
  statusCode?: number;
}

interface RtkQueryLikeError {
  status?: number | string;
  data?: BackendErrorBody;
}

const OUR_AUTH_ERROR_NAMES = new Set(['UnauthorizedError']);

/** Names of errors that are known to originate from OUR OWN request validation (safe to show). */
const OUR_VALIDATION_ERROR_NAMES = new Set(['BadRequestError', 'ValidationError', 'UnprocessableEntityError']);

const getBody = (err: unknown): BackendErrorBody | undefined => (err as RtkQueryLikeError)?.data;

const getHttpStatus = (err: unknown): number | undefined => {
  const status = (err as RtkQueryLikeError)?.status;
  return typeof status === 'number' ? status : undefined;
};

/**
 * Classifies a PandaDoc API error so the UI can react appropriately instead of
 * rendering a raw AxiosError / bare status code.
 */
export const classifyPandaDocError = (err: unknown): PandaDocErrorKind => {
  const body = getBody(err);
  const status = getHttpStatus(err) ?? body?.statusCode;
  const name = body?.name;

  if (status === 401 || status === 403) {
    // Only trust a 401/403 as "your session expired" when it demonstrably came from
    // our own auth middleware. Anything else (most notably a proxied AxiosError) is
    // an upstream integration problem, not something re-logging in will fix.
    return name && OUR_AUTH_ERROR_NAMES.has(name) ? 'sessionExpired' : 'integrationUnavailable';
  }

  if (name === 'AxiosError') return 'integrationUnavailable';

  if (status != null && status >= 500) return 'integrationUnavailable';

  if (status === 400 && (!name || OUR_VALIDATION_ERROR_NAMES.has(name))) return 'requestInvalid';

  return 'unknown';
};

/**
 * Safe, user-facing message for a PandaDoc API error. Never surfaces the raw backend
 * payload (e.g. "Request failed with status code 401") for anything other than our
 * own request-validation errors, where the message is written for end users.
 */
export const pandaDocErrorMessage = (
  err: unknown,
  copy: { sessionExpired: string; integrationUnavailable: string; fallback: string }
): string => {
  const kind = classifyPandaDocError(err);
  const body = getBody(err);

  switch (kind) {
    case 'sessionExpired':
      return copy.sessionExpired;
    case 'integrationUnavailable':
      return copy.integrationUnavailable;
    case 'requestInvalid':
      return body?.message?.trim() || copy.fallback;
    default:
      return copy.fallback;
  }
};
